import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { RefreshCcw, X } from "lucide-react";

/**
 * Polls /api/version every POLL_MS. When the build id rotates (i.e. a redeploy
 * happened), shows a non-blocking banner inviting the user to refresh. This
 * stops the recurring complaint of "missing features" caused by stale browser
 * caches holding the previous bundle.
 */
const POLL_MS = 2 * 60 * 1000;   // 2 minutes
const GRACE_MS = 30 * 1000;      // ignore the first 30s after mount

export default function VersionChecker() {
    const [stale, setStale] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const baseline = useRef(null);
    const mountedAt = useRef(Date.now());

    useEffect(() => {
        let cancelled = false;

        const check = async () => {
            try {
                const { data } = await api.get("/version");
                if (cancelled || !data?.build) return;
                if (baseline.current === null) {
                    baseline.current = data.build;
                    return;
                }
                if (data.build !== baseline.current && Date.now() - mountedAt.current > GRACE_MS) {
                    setStale(true);
                }
            } catch { /* offline or 429 — silently retry next tick */ }
        };

        check();
        const id = setInterval(check, POLL_MS);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const refresh = async () => {
        try {
            if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k)));
            }
        } catch { /* ignore */ }
        window.location.reload();
    };

    if (!stale || dismissed) return null;

    return (
        <div data-testid="version-banner"
             className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] card-warm px-4 py-3 shadow-lg flex items-center gap-3 max-w-md w-[calc(100%-2rem)]">
            <div className="flex-1 min-w-0">
                <div className="font-serif text-base leading-tight">A fresh build is ready</div>
                <div className="text-xs text-[var(--text-ink)]/65">Refresh to load the latest features and fixes.</div>
            </div>
            <button onClick={refresh} data-testid="version-refresh-btn"
                    className="btn-primary text-xs inline-flex items-center gap-1.5 px-3 py-1.5">
                <RefreshCcw size={12} /> Refresh
            </button>
            <button onClick={() => setDismissed(true)} data-testid="version-dismiss-btn"
                    title="Dismiss" className="btn-ghost text-[var(--text-ink)]/55 hover:text-[var(--text-ink)] p-1">
                <X size={14} />
            </button>
        </div>
    );
}
